<template>
  <div class="health-workbench">
    <workbench-banner :flow-text="flowText" :flow-hint="flowHint" />
    <section-nav :sections="sections" @scroll="scrollTo" />

    <div v-loading="loading" class="workspace">
      <member-list
        v-model="keyword"
        :members="members"
        :selected-id="selectedId"
        @search="searchMembers"
        @select="selectMember"
      />

      <div v-if="member" class="detail">
        <member-profile :member="member" :active-count="activeMeds.length" />
        <health-report :member="member" :generating="reportGenerating" @generate="generateReport" />
        <member-labels
          :member="member"
          :generating="labelGenerating"
          :adding="labelAdding"
          :removing="labelRemoving"
          @generate="generateLabels"
          @adopt="adoptRecommend"
          @ignore="ignoreRecommend"
          @pick="pickLabel"
          @remove="removeLabel"
        />
        <medication-record
          ref="medication"
          :key="member.id"
          :member="member"
          :active-count="activeMeds.length"
          :saving="savingMed"
          @save="addMedication"
          @stop="stopMedication"
        />
        <health-scheme :member="member" @changed="refreshProfile" />
        <div class="done">
          <div>
            <strong>本次健康管理流程已完成</strong>
            <p>报告、标签、用药和方案已写入 {{ member.name }} 的统一健康档案，后续数据将自动实时更新。</p>
          </div>
          <button type="button" @click="scrollTo('report')">返回顶部复核</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 一体化健康管理工作台
 * 页面只负责拉数和提交：会员列表 → 点选会员 → 全景档案 + 用药台账
 * 四个区块（报告 / 标签 / 用药 / 方案）拆在 components 下
 * 方案的发放、终止、进度和今日任务由健康方案组件自己请求
 */
import {
  listWorkbenchMembers,
  getWorkbenchProfile,
  generateHealthReport,
  generateLabelRecommend,
  adoptLabelRecommend,
  ignoreLabelRecommend,
  addMemberLabelRelation,
  removeMemberLabelRelation,
  listMemberMedication,
  saveMemberMedication,
  stopMemberMedication
} from '@/api/healthManage/index';
import WorkbenchBanner from './components/WorkbenchBanner.vue';
import SectionNav from './components/SectionNav.vue';
import MemberList from './components/MemberList.vue';
import MemberProfile from './components/MemberProfile.vue';
import HealthReport from './components/HealthReport.vue';
import MemberLabels from './components/MemberLabels.vue';
import MedicationRecord from './components/MedicationRecord.vue';
import HealthScheme from './components/HealthScheme.vue';

/** 频次对应每日次数和提醒时间点，数量必须等于 timesPerDay，格式 HH:mm */
const REMIND_MAP = {
  '每日1次': { timesPerDay: 1, remindTimes: '08:00' },
  '每日2次': { timesPerDay: 2, remindTimes: '08:00,20:00' },
  '每日3次': { timesPerDay: 3, remindTimes: '08:00,12:00,20:00' },
  '按需服用': { timesPerDay: 1, remindTimes: '08:00' }
};

export default {
  name: 'health_workbench',
  components: {
    WorkbenchBanner,
    SectionNav,
    MemberList,
    MemberProfile,
    HealthReport,
    MemberLabels,
    MedicationRecord,
    HealthScheme
  },
  data() {
    return {
      loading: false,
      keyword: '',
      members: [],
      profile: null,
      selectedId: null,
      reportGenerating: false,
      labelGenerating: false,
      labelAdding: false,
      labelRemoving: false,
      savingMed: false,
      sections: [
        { id: 'report', label: '健康报告', desc: 'AI综合评估' },
        { id: 'tags', label: '用户标签', desc: '动态更新画像' },
        { id: 'medication', label: '用药记录', desc: '处方与依从性' },
        { id: 'plan', label: '健康方案', desc: '任务分配执行' }
      ]
    };
  },
  computed: {
    /** 当前选中会员的全景档案 */
    member() {
      return this.profile;
    },
    /** status=1 为服用中 */
    activeMeds() {
      if (!this.member) return [];
      return (this.member.medications || []).filter((item) => item.status == 1);
    },
    /** 流程完整度由后端计算（0/25/50/75/100），前端不自己算 */
    flowText() {
      if (!this.member) return '--';
      return `${this.member.flowCompleteness || 0}%`;
    },
    flowHint() {
      const steps = (this.member && this.member.steps) || [];
      const done = steps.filter((item) => item.done).length;
      return `${done}个环节已完成`;
    }
  },
  created() {
    this.searchMembers();
  },
  methods: {
    /** 列表按当前登录药企、门店过滤 */
    loginScope() {
      let loginData = {};
      try {
        loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
      } catch (e) {
        loginData = {};
      }
      const user = loginData.user || loginData;
      return {
        enterpriseId: user.enterpriseId || loginData.enterpriseId,
        storeId: user.storeId || loginData.storeId
      };
    },
    /** 搜索会员；有结果时保持当前选中，否则默认打开第一位 */
    searchMembers() {
      this.loading = true;
      const scope = this.loginScope();
      listWorkbenchMembers({
        enterpriseId: scope.enterpriseId,
        storeId: scope.storeId,
        keyword: (this.keyword || '').trim() || undefined,
        pageNum: 1,
        pageSize: 20
      }).then((res) => {
        const page = res.data || {};
        this.members = (page.records || []).map((item) => ({
          id: item.memberId,
          name: item.memberName,
          phone: item.photoNumber,
          score: item.totalScore == null ? '--' : item.totalScore,
          risk: item.riskText || '无报告',
          riskLevel: item.riskLevel
        }));
        if (!this.members.length) {
          this.selectedId = null;
          this.profile = null;
          this.loading = false;
          return;
        }
        const still = this.members.some((item) => item.id === this.selectedId);
        this.selectMember(still ? this.selectedId : this.members[0].id);
      }).catch(() => {
        this.loading = false;
      });
    },
    /** 进入详情：档案和用药台账一起拉，用药列表不在档案接口里 */
    selectMember(id) {
      this.selectedId = id;
      this.loading = true;
      Promise.all([
        getWorkbenchProfile(id),
        listMemberMedication(id)
      ]).then(([profileRes, medRes]) => {
        this.profile = this.mapProfile(profileRes.data || {}, medRes.data || []);
      }).finally(() => {
        this.loading = false;
      });
    },
    /** 把档案接口字段收成页面用的结构；无评分时展示「无报告」 */
    mapProfile(data, medications) {
      const report = data.report || {};
      const label = data.label || {};
      const medication = data.medication || {};
      return {
        id: data.memberId,
        name: data.memberName,
        phone: data.photoNumber,
        score: data.totalScore == null ? '--' : data.totalScore,
        risk: data.riskText || '无报告',
        riskLevel: data.riskLevel,
        hasReport: data.totalScore != null || !!report.updateTime,
        tags: label.currentLabels || [],
        recommends: label.recommends || [],
        lastAutoUpdateText: label.lastAutoUpdateText,
        lastAutoUpdateTime: label.lastAutoUpdateTime,
        plan: data.latestSchemeName,
        runningSchemeCount: data.runningSchemeCount || 0,
        flowCompleteness: data.flowCompleteness || 0,
        steps: data.steps || [],
        updatedAt: report.updateTime || '',
        conclusion: report.aiConclusion || '暂无健康结论，可先生成报告。',
        medToday: medication.todayText || '--',
        medAdherence: medication.recent7AdherenceRate == null ? '--' : `${medication.recent7AdherenceRate}%`,
        medNext: medication.nextRemindTime || '--',
        indicators: report.indicators || [],
        medications: medications || [],
        labelCount: data.labelCount || 0,
        takingDrugCount: data.takingDrugCount || 0
      };
    },
    refreshProfile() {
      if (this.selectedId) this.selectMember(this.selectedId);
    },
    scrollTo(id) {
      const el = document.getElementById(`health-${id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    /** 生成报告后评分会变，需要同时刷新左侧列表和右侧档案 */
    generateReport() {
      if (!this.selectedId) return;
      this.reportGenerating = true;
      generateHealthReport(this.selectedId).then(() => {
        this.$message.success('报告已生成');
        this.searchMembers();
      }).finally(() => {
        this.reportGenerating = false;
      });
    },
    /** 推荐生成后只刷新当前会员档案 */
    generateLabels() {
      if (!this.selectedId) return;
      this.labelGenerating = true;
      generateLabelRecommend(this.selectedId).then((res) => {
        this.$message.success(`本次新增 ${res.data || 0} 条推荐`);
        this.refreshProfile();
      }).finally(() => {
        this.labelGenerating = false;
      });
    },
    adoptRecommend(item) {
      adoptLabelRecommend(item.id).then(() => {
        this.$message.success('已采纳标签');
        this.refreshProfile();
      });
    },
    ignoreRecommend(item) {
      ignoreLabelRecommend(item.id).then(() => {
        this.$message.success('已忽略推荐');
        this.refreshProfile();
      });
    },
    /** 从标签字典给会员加标签 */
    pickLabel(item) {
      if (!this.member || !item || item.id == null) return;
      this.labelAdding = true;
      addMemberLabelRelation({
        memberId: this.member.id,
        labelId: item.id
      }).then(() => {
        this.$message.success(`已添加「${item.labelName}」`);
        this.refreshProfile();
      }).finally(() => {
        this.labelAdding = false;
      });
    },
    /** 删除当前已生效标签，path 用 labelId */
    removeLabel(tag) {
      if (!this.member) return;
      const labelId = tag.labelId != null ? tag.labelId : tag.id;
      if (labelId == null) {
        this.$message.warning('缺少标签ID，无法删除');
        return;
      }
      this.$confirm(`确认移除标签「${tag.labelName}」？`, '提示', { type: 'warning' }).then(() => {
        this.labelRemoving = true;
        removeMemberLabelRelation(this.member.id, labelId).then(() => {
          this.$message.success('标签已移除');
          this.refreshProfile();
        }).finally(() => {
          this.labelRemoving = false;
        });
      }).catch(() => {});
    },
    /**
     * 手动新增用药。sourceType=1 表示手动录入。
     * 当天新增没有打卡明细，次日 00:10 才会展开，所以提示从明日开始打卡。
     */
    addMedication(form) {
      if (!this.member) return;
      const name = (form.name || '').trim();
      const dosage = (form.dosage || '').trim();
      if (!name) {
        this.$message.warning('请填写药品名称');
        return;
      }
      const remind = REMIND_MAP[form.frequency] || REMIND_MAP['每日1次'];
      const scope = this.loginScope();
      const today = new Date();
      const startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      this.savingMed = true;
      saveMemberMedication({
        memberId: this.member.id,
        drugName: name,
        dosage,
        frequency: form.frequency,
        timesPerDay: remind.timesPerDay,
        remindTimes: remind.remindTimes,
        startDate,
        sourceType: 1,
        enterpriseId: scope.enterpriseId,
        storeId: scope.storeId
      }).then(() => {
        this.$message.success('用药记录已保存，打卡从明日开始');
        if (this.$refs.medication) this.$refs.medication.resetForm();
        this.refreshProfile();
      }).finally(() => {
        this.savingMed = false;
      });
    },
    stopMedication(id) {
      this.$confirm('确认将该药品标记为已停药？', '提示', { type: 'warning' }).then(() => {
        stopMemberMedication(id).then(() => {
          this.$message.success('已停药');
          this.refreshProfile();
        });
      }).catch(() => {});
    }
  }
};
</script>

<style lang="less" src="./workbench.less"></style>
